import { z } from 'zod';
import { BusinessCodeSchema, EnteredNameSchema, NoteSchema, RecordIdSchema } from './identifiers';
import { InvoiceLineInputSchema, InvoiceLineSchema, QuantitySchema } from './invoicing';
import {
  DecimalStringSchema,
  MoneySchema,
  compareMoney,
  divideMoney,
  money,
  multiplyMoney,
  roundMoney,
  subtractMoney,
  type Money,
} from './money';
import { BudgetRefSchema } from './budgets';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Vendors, procurement and payables — BMP-2 package 4 (`PROC-*`, `FIN-INVOICE-001`, `FIN-ARAP-002`,
 * `FIN-REPORT-006`).
 *
 * The chain is request → (quotations) → purchase order → receipt → vendor invoice → payment request.
 * Every step is a record with its own state, numbered, audited and scoped by legal entity and branch.
 * Nothing here moves money: a vendor invoice reports `vendorInvoice.recorded` to the posting engine,
 * and paying it is a treasury payment request to the vendor's payee, approved and executed under the
 * treasury rules (BD-59, BD-60).
 *
 * Client policy stays configuration: no approval threshold, budget, match tolerance or retention is
 * invented. Where a decision is open the conservative rule applies and names it — exact three-way
 * match (`BD-47`), all five procurement roles distinct (`BD-50`), budget control "not configured" until
 * the client chooses its policy (`BD-49`).
 */

export const PROCUREMENT_SCOPE_NOTE = 'legalEntity-branch' as const;

const ReasonSchema = z.string().trim().min(3).max(500);
const IdempotencyKeySchema = z.string().trim().min(8).max(200);
const UnitSchema = z.string().trim().min(1).max(20);

/* ------------------------------------------------------- retention (BD-51) */

/**
 * Retention held on an order's invoices (PROC-PO-004). Entered per order, never defaulted: the
 * percentage and the amount it applies to (`net` before tax, or `gross`) are both stated, because
 * BD-51 leaves them to each contract.
 */
export const RETENTION_BASES = ['net', 'gross'] as const;
export const RetentionBaseSchema = z.enum(RETENTION_BASES);
export const PercentRateSchema = z
  .string()
  .regex(/^\d{1,2}(\.\d{1,4})?$/, { message: 'PERCENT_EXPECTED' })
  .refine((value) => Number(value) > 0 && Number(value) < 100, { message: 'PERCENT_EXPECTED' });
export const RetentionTermsSchema = z.strictObject({
  percent: PercentRateSchema,
  base: RetentionBaseSchema,
});
export type RetentionTerms = z.infer<typeof RetentionTermsSchema>;

/** A percentage of an amount, rounded half-up to two places — exact decimal arithmetic. */
export function percentOf(amount: Money, percent: string): Money {
  const factor = divideMoney(money(percent, 'XXX'), '100', 10, 'halfUp').amount;
  return roundMoney(multiplyMoney(amount, factor), 2, 'halfUp');
}

/** Retention on an invoice under an order's terms. */
export function retentionAmount(
  terms: RetentionTerms,
  totals: { net: Money; gross: Money },
): Money {
  return percentOf(terms.base === 'net' ? totals.net : totals.gross, terms.percent);
}

/* -------------------------------------------------------------------- vendors */

export const VENDOR_KINDS = ['supplier', 'contractor', 'serviceProvider', 'consultant'] as const;
export const VendorKindSchema = z.enum(VENDOR_KINDS);

export const VENDOR_STATES = ['active', 'suspended', 'blocked'] as const;
export const VendorStateSchema = z.enum(VENDOR_STATES);
export type VendorState = z.infer<typeof VendorStateSchema>;

export const VENDOR_QUALIFICATIONS = [
  'notAssessed',
  'qualified',
  'conditional',
  'rejected',
] as const;
export const VendorQualificationSchema = z.enum(VENDOR_QUALIFICATIONS);

const ContactSchema = z.strictObject({
  person: z.string().trim().max(120).optional(),
  phone: z.string().trim().max(40).optional(),
  email: z.string().trim().email().max(254).optional(),
  address: z.string().trim().max(400).optional(),
});

/** One person the company deals with at the supplier; `authorized` may sign or commit for it. */
export const VendorContactPersonSchema = z.strictObject({
  name: z.string().trim().min(2).max(120),
  role: z.string().trim().max(80).optional(),
  phone: z.string().trim().max(40).optional(),
  email: z.string().trim().email().max(254).optional(),
  authorized: z.boolean().default(false),
});

export const VendorAddressesSchema = z.strictObject({
  registered: z.string().trim().max(400).optional(),
  correspondence: z.string().trim().max(400).optional(),
  delivery: z.string().trim().max(400).optional(),
});

/**
 * What a supplier record still lacks, and which transaction each gap blocks. A draft supplier saves
 * without any of it (owner instruction 2026-10-17); the gap shows where it matters.
 */
export const VENDOR_PREREQUISITES = [
  'payee',
  'taxRegistration',
  'commercialRegistration',
  'authorizedContact',
] as const;
export const VendorReadinessSchema = z.array(
  z.strictObject({
    item: z.enum(VENDOR_PREREQUISITES),
    complete: z.boolean(),
    blocks: z.array(z.enum(['payment', 'taxInvoice', 'award', 'order'])),
  }),
);

export const VendorSchema = z.strictObject({
  vendorId: RecordIdSchema,
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  /** The supplier's name in the other language, when it has one. */
  alternateName: EnteredNameSchema.optional(),
  kind: VendorKindSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  /**
   * Shown in full only to holders of `procurement.vendor.viewIdentifiers`; others see the last four
   * characters behind a mask (`identifiersMasked`).
   */
  taxRegistration: z.string().max(60).optional(),
  commercialRegistration: z.string().max(60).optional(),
  identifiersMasked: z.boolean(),
  contact: ContactSchema,
  contacts: z.array(VendorContactPersonSchema).max(10),
  addresses: VendorAddressesSchema,
  readiness: VendorReadinessSchema,
  categories: z.array(z.string().trim().min(1).max(60)).max(20),
  state: VendorStateSchema,
  qualification: VendorQualificationSchema,
  qualificationNote: NoteSchema.optional(),
  /** The treasury payee that holds the vendor's bank details and receives its payments. */
  payeeId: RecordIdSchema.optional(),
  notes: NoteSchema.optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Vendor = z.infer<typeof VendorSchema>;

export const CreateVendorSchema = z.strictObject({
  code: BusinessCodeSchema,
  name: EnteredNameSchema,
  alternateName: EnteredNameSchema.optional(),
  kind: VendorKindSchema,
  branchId: RecordIdSchema,
  taxRegistration: z.string().trim().max(60).optional(),
  commercialRegistration: z.string().trim().max(60).optional(),
  contact: ContactSchema.default({}),
  contacts: z.array(VendorContactPersonSchema).max(10).default([]),
  addresses: VendorAddressesSchema.default({}),
  categories: z.array(z.string().trim().min(1).max(60)).max(20).default([]),
  notes: NoteSchema.optional(),
});
export type CreateVendor = z.infer<typeof CreateVendorSchema>;
export type CreateVendorInput = z.input<typeof CreateVendorSchema>;

export const UpdateVendorSchema = z.strictObject({
  name: EnteredNameSchema.optional(),
  alternateName: EnteredNameSchema.nullable().optional(),
  kind: VendorKindSchema.optional(),
  taxRegistration: z.string().trim().max(60).optional(),
  commercialRegistration: z.string().trim().max(60).optional(),
  contact: ContactSchema.optional(),
  contacts: z.array(VendorContactPersonSchema).max(10).optional(),
  addresses: VendorAddressesSchema.optional(),
  categories: z.array(z.string().trim().min(1).max(60)).max(20).optional(),
  qualification: VendorQualificationSchema.optional(),
  qualificationNote: NoteSchema.optional(),
  notes: NoteSchema.optional(),
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});
export type UpdateVendor = z.infer<typeof UpdateVendorSchema>;

export const ChangeVendorStateSchema = z.strictObject({
  state: VendorStateSchema,
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});

/**
 * Link the vendor to a treasury payee — an existing one of the same legal entity, or a new one
 * created from the vendor (cash-only, or with bank details that then wait for approval, BD-60).
 */
export const LinkVendorPayeeSchema = z
  .strictObject({
    payeeId: RecordIdSchema.optional(),
    create: z
      .strictObject({
        bankName: z.string().trim().max(120).optional(),
        accountNumber: z.string().trim().max(64).optional(),
        iban: z.string().trim().max(64).optional(),
        reason: ReasonSchema.optional(),
      })
      .optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => (value.payeeId === undefined) !== (value.create === undefined), {
    message: 'PAYEE_OR_CREATE_REQUIRED',
  });

export const VendorQuerySchema = z.strictObject({
  search: z.string().trim().max(80).optional(),
  state: VendorStateSchema.optional(),
  kind: VendorKindSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type VendorQuery = z.infer<typeof VendorQuerySchema>;

export const VendorPageSchema = z.strictObject({
  items: z.array(VendorSchema),
  total: z.number().int().nonnegative(),
  nextCursor: z.string().optional(),
});

/* ----------------------------------------------------------- purchase requests */

export const PURCHASE_REQUEST_STATES = [
  'draft',
  'pendingApproval',
  'approved',
  'rejected',
  'ordered',
  'closed',
  'cancelled',
] as const;
export const PurchaseRequestStateSchema = z.enum(PURCHASE_REQUEST_STATES);
export type PurchaseRequestState = z.infer<typeof PurchaseRequestStateSchema>;

export const RequestLineInputSchema = z.strictObject({
  description: z.string().trim().min(2).max(300),
  quantity: QuantitySchema,
  unit: UnitSchema,
  /** An estimate only, for the budget check and the approver; never a price commitment. */
  estimatedUnitPrice: DecimalStringSchema.optional(),
  /** A catalogue item, when stores are in use (WH-ITEM-001). */
  itemId: RecordIdSchema.optional(),
});
export type RequestLineInput = z.infer<typeof RequestLineInputSchema>;

export const RequestLineSchema = RequestLineInputSchema.extend({
  lineNo: z.number().int().positive(),
  /** Quantity already placed on purchase orders. */
  orderedQuantity: DecimalStringSchema,
});

/**
 * The budget check recorded on a request or order (BD-49): what the policy said when it was submitted.
 * `notConfigured` — no policy chosen, nothing enforced; `noBudgetLine` — the purchase names no line.
 */
export const BudgetCheckSchema = z.strictObject({
  status: z.enum([
    'notConfigured',
    'noBudgetLine',
    'withinBudget',
    'exceedsBudget',
    'overspendApproved',
  ]),
  decision: z.literal('BD-49'),
  /** What the line had available when checked, and what the purchase asked. */
  available: MoneySchema.optional(),
  requested: MoneySchema.optional(),
  /** The policy that applied: refuse, warn or approval. */
  onExceed: z.enum(['refuse', 'warn', 'approval']).optional(),
  /** The overspend approval the purchase waits for, when `onExceed` is `approval`. */
  overspendApprovalRequestId: z.string().optional(),
  checkedAt: InstantSchema.optional(),
});
export type BudgetCheck = z.infer<typeof BudgetCheckSchema>;

export const PurchaseRequestSchema = z.strictObject({
  requestId: RecordIdSchema,
  number: z.string(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  costCenter: z.string().max(40).optional(),
  purpose: z.string().max(1000),
  neededBy: BusinessDateSchema.optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  lines: z.array(RequestLineSchema),
  estimatedTotal: MoneySchema.optional(),
  /** The budget line the purchase is charged to (FIN-BUDGET-002). */
  budget: BudgetRefSchema.optional(),
  budgetCheck: BudgetCheckSchema,
  state: PurchaseRequestStateSchema,
  approvalRequestId: z.string().optional(),
  requestedByAccountId: z.string(),
  decisionReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type PurchaseRequest = z.infer<typeof PurchaseRequestSchema>;

export const CreatePurchaseRequestSchema = z.strictObject({
  budget: BudgetRefSchema.optional(),
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  costCenter: z.string().trim().max(40).optional(),
  purpose: z.string().trim().min(3).max(1000),
  neededBy: BusinessDateSchema.optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  lines: z.array(RequestLineInputSchema).min(1).max(100),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreatePurchaseRequest = z.infer<typeof CreatePurchaseRequestSchema>;

export const SubmitSchema = z.strictObject({ expectedVersion: z.number().int().positive() });
export const CancelSchema = z.strictObject({
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});

export const ProcurementQuerySchema = z.strictObject({
  state: z.string().max(40).optional(),
  vendorId: RecordIdSchema.optional(),
  projectId: RecordIdSchema.optional(),
  /** Vendor invoices of one purchase order (the order page lists them). */
  orderId: RecordIdSchema.optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
  cursor: z.string().min(1).max(200).optional(),
});
export type ProcurementQuery = z.infer<typeof ProcurementQuerySchema>;

/* ---------------------------------------------------- requests for quotation */

/**
 * `awardPending`: an award waits for its approval (`procurement.award.approve`). Awards are per supplier:
 * the whole quotation revision of one supplier is awarded — splitting lines between suppliers is not
 * part of the procurement specification and is not offered.
 */
export const RFQ_STATES = ['open', 'awardPending', 'awarded', 'cancelled'] as const;
export const RfqStateSchema = z.enum(RFQ_STATES);

const PercentSchema = z
  .string()
  .regex(/^\d{1,3}(\.\d{1,4})?$/, { message: 'PERCENT_EXPECTED' })
  .refine((value) => Number(value) <= 100, { message: 'PERCENT_EXPECTED' });

/** One quoted line: what the supplier offers against a request line, as the supplier wrote it. */
export const QuoteLineSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  /** The quantity offered — like for like only when it equals the request's. */
  quantity: DecimalStringSchema,
  unitPrice: DecimalStringSchema,
  discountPercent: z.string().optional(),
  taxRatePercent: z.string().optional(),
  /** The supplier's specification, where it differs from or details the request's. */
  specification: z.string().max(300).optional(),
  /** False when the offer does not meet the requested specification. */
  complies: z.boolean(),
  /** Unit price after the discount, and the line's amounts (computed, never typed). */
  netUnitPrice: DecimalStringSchema,
  net: DecimalStringSchema,
  tax: DecimalStringSchema,
});

export const QuoteTotalsSchema = z.strictObject({
  subtotal: MoneySchema,
  discount: MoneySchema,
  net: MoneySchema,
  tax: MoneySchema,
  charges: MoneySchema,
  total: MoneySchema,
});

export const VendorQuoteSchema = z.strictObject({
  vendorId: RecordIdSchema,
  /** 1 for the first quotation; each correction or re-quote is a new revision, the old ones kept. */
  revision: z.number().int().positive(),
  quoteNumber: z.string().max(60).optional(),
  receivedOn: BusinessDateSchema,
  currency: z.string(),
  /** One price per request line, by line number. A line the vendor cannot supply is omitted. */
  prices: z.array(
    z.strictObject({ lineNo: z.number().int().positive(), unitPrice: DecimalStringSchema }),
  ),
  lines: z.array(QuoteLineSchema),
  charges: z.strictObject({ freight: DecimalStringSchema, other: DecimalStringSchema }),
  totals: QuoteTotalsSchema,
  deliveryDays: z.number().int().nonnegative().max(3650).optional(),
  validUntil: BusinessDateSchema.optional(),
  paymentTerms: z.string().max(300).optional(),
  warranty: z.string().max(300).optional(),
  notes: NoteSchema.optional(),
  total: MoneySchema,
  /** True when the vendor quoted every line, in full quantity, meeting the specification. */
  complete: z.boolean(),
  recordedByAccountId: z.string(),
  recordedAt: InstantSchema,
});
export type VendorQuote = z.infer<typeof VendorQuoteSchema>;

export const AWARD_STATES = ['pendingApproval', 'approved', 'rejected', 'invalidated'] as const;

/**
 * An award: one supplier's quotation revision, with a fingerprint of everything the approval covers —
 * supplier, revision, quantities, prices, discounts, taxes and charges. A change to any quote after
 * the award invalidates it; the order is generated from these values only.
 */
export const RfqAwardSchema = z.strictObject({
  vendorId: RecordIdSchema,
  revision: z.number().int().positive(),
  total: MoneySchema,
  fingerprint: z.string(),
  lowest: z.boolean(),
  reason: z.string().optional(),
  state: z.enum(AWARD_STATES),
  approvalRequestId: z.string().optional(),
  awardedByAccountId: z.string(),
  awardedAt: InstantSchema,
  decidedAt: InstantSchema.optional(),
  invalidatedReason: z.string().optional(),
  orderId: RecordIdSchema.optional(),
});
export type RfqAward = z.infer<typeof RfqAwardSchema>;

export const RfqSchema = z.strictObject({
  rfqId: RecordIdSchema,
  number: z.string(),
  requestId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  currency: z.string(),
  invitedVendorIds: z.array(RecordIdSchema),
  dueOn: BusinessDateSchema.optional(),
  quotes: z.array(VendorQuoteSchema),
  /** Earlier revisions, kept as they were. */
  quoteHistory: z.array(VendorQuoteSchema),
  state: RfqStateSchema,
  awardedVendorId: RecordIdSchema.optional(),
  awardReason: z.string().optional(),
  award: RfqAwardSchema.optional(),
  /** Earlier awards that were rejected or invalidated. */
  awardHistory: z.array(RfqAwardSchema),
  /** Abandoned with a reason — the quotation route ended; a direct purchase may follow. */
  cancellation: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  createdByAccountId: z.string(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type Rfq = z.infer<typeof RfqSchema>;

export const CreateRfqSchema = z.strictObject({
  requestId: RecordIdSchema,
  vendorIds: z.array(RecordIdSchema).min(1).max(20),
  dueOn: BusinessDateSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});

export const QuoteLineInputSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  unitPrice: DecimalStringSchema,
  /** Defaults to the request line's quantity. */
  quantity: QuantitySchema.optional(),
  discountPercent: PercentSchema.optional(),
  taxRatePercent: PercentSchema.optional(),
  specification: z.string().trim().max(300).optional(),
  complies: z.boolean().default(true),
});

export const RecordQuoteSchema = z
  .strictObject({
    vendorId: RecordIdSchema,
    receivedOn: BusinessDateSchema,
    quoteNumber: z.string().trim().max(60).optional(),
    /** Defaults to the request's currency; another currency is recorded but never compared. */
    currency: z
      .string()
      .regex(/^[A-Z]{3}$/)
      .optional(),
    /** The simple form: one unit price per request line. */
    prices: z
      .array(
        z.strictObject({ lineNo: z.number().int().positive(), unitPrice: DecimalStringSchema }),
      )
      .min(1)
      .max(100)
      .optional(),
    /** The full form: quantity, discount, tax and specification per line. */
    lines: z.array(QuoteLineInputSchema).min(1).max(100).optional(),
    freight: DecimalStringSchema.optional(),
    otherCharges: DecimalStringSchema.optional(),
    deliveryDays: z.number().int().nonnegative().max(3650).optional(),
    validUntil: BusinessDateSchema.optional(),
    paymentTerms: z.string().trim().max(300).optional(),
    warranty: z.string().trim().max(300).optional(),
    notes: NoteSchema.optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => (value.prices === undefined) !== (value.lines === undefined), {
    message: 'PRICES_OR_LINES_REQUIRED',
  });
export type RecordQuote = z.input<typeof RecordQuoteSchema>;

export const AwardRfqSchema = z.strictObject({
  vendorId: RecordIdSchema,
  /** The revision the person compared; a newer one refuses the award (QUOTE_REVISION_CHANGED). */
  revision: z.number().int().positive().optional(),
  /** Required when the awarded quote is not the lowest comparable one. */
  reason: z.string().trim().max(500).optional(),
  expectedVersion: z.number().int().positive(),
});

/** Generate the purchase order from the approved award — values come from the award, never typed. */
export const OrderFromAwardSchema = z.strictObject({
  deliveryDate: BusinessDateSchema.optional(),
  terms: NoteSchema.optional(),
  retention: RetentionTermsSchema.optional(),
  idempotencyKey: IdempotencyKeySchema,
});

/**
 * Price a quotation exactly: per line, gross = quantity × unit price, net unit price = unit price less
 * the discount, net = quantity × net unit price (rounded to cents half-up, as an order line is), tax =
 * net × rate; totals add the charges. The same arithmetic prices the order generated from an award, so
 * the order total equals the approved net to the cent.
 */
export function priceQuoteLine(
  quantity: string,
  unitPrice: string,
  discountPercent: string | undefined,
  taxRatePercent: string | undefined,
  currency: string,
): { netUnitPrice: string; gross: Money; net: Money; tax: Money } {
  const price = money(unitPrice, currency);
  const hundred = money('100', currency);
  // unit price × (100 − discount) ÷ 100: exact for any discount with up to four decimals.
  const netUnit = discountPercent
    ? divideMoney(
        multiplyMoney(price, subtractMoney(hundred, money(discountPercent, currency)).amount),
        '100',
        8,
        'halfUp',
      )
    : price;
  const gross = roundMoney(multiplyMoney(price, quantity), 2, 'halfUp');
  const net = roundMoney(multiplyMoney(netUnit, quantity), 2, 'halfUp');
  const tax = taxRatePercent
    ? roundMoney(divideMoney(multiplyMoney(net, taxRatePercent), '100', 8, 'halfUp'), 2, 'halfUp')
    : money('0', currency);
  return { netUnitPrice: netUnit.amount, gross, net, tax };
}

/**
 * Why a quote is left out of the "lowest" comparison: it does not cover every line in full
 * (`incomplete`), it is in another currency (`currency`), its validity has passed (`expired`), or
 * an offered item does not meet the specification (`nonCompliant`). Such a quote is still shown, with
 * its reasons, and may still be awarded with a written reason — it is simply never called cheapest.
 */
export const QUOTE_EXCLUSIONS = ['incomplete', 'currency', 'expired', 'nonCompliant'] as const;
export type QuoteExclusion = (typeof QUOTE_EXCLUSIONS)[number];

export const QuoteComparisonRowSchema = z.strictObject({
  vendorId: z.string(),
  revision: z.number().int().positive(),
  currency: z.string(),
  total: DecimalStringSchema,
  net: DecimalStringSchema,
  tax: DecimalStringSchema,
  charges: DecimalStringSchema,
  complete: z.boolean(),
  comparable: z.boolean(),
  excludedBecause: z.array(z.enum(QUOTE_EXCLUSIONS)),
  lowest: z.boolean(),
  deliveryDays: z.number().int().nonnegative().optional(),
  validUntil: BusinessDateSchema.optional(),
});
export type QuoteComparisonRow = z.infer<typeof QuoteComparisonRowSchema>;

/** Side by side per request line: each supplier's offer, the lowest comparable net marked. */
export const QuoteLineComparisonSchema = z.strictObject({
  lineNo: z.number().int().positive(),
  offers: z.array(
    z.strictObject({
      vendorId: z.string(),
      quantity: DecimalStringSchema,
      netUnitPrice: DecimalStringSchema,
      net: DecimalStringSchema,
      taxRatePercent: z.string().optional(),
      complies: z.boolean(),
      specification: z.string().optional(),
      lowest: z.boolean(),
    }),
  ),
});

/**
 * The comparison: one row per supplier's current revision, and per request line. Only like-for-like
 * quotes compete for "lowest": the request's currency, every line in full quantity, meeting the
 * specification, still valid on `today`. Pure, shown to everyone who reads the request.
 */
export function quoteComparison(
  rfq: Pick<Rfq, 'quotes' | 'currency'>,
  requestLines: readonly { lineNo: number; quantity: string }[] = [],
  today?: string,
): QuoteComparisonRow[] {
  const rows = rfq.quotes.map((quote) => {
    const excludedBecause: QuoteExclusion[] = [];
    if (!quote.complete) excludedBecause.push('incomplete');
    if (quote.currency !== rfq.currency) excludedBecause.push('currency');
    if (today && quote.validUntil && quote.validUntil < today) excludedBecause.push('expired');
    if (quote.lines.some((line) => !line.complies)) excludedBecause.push('nonCompliant');
    if (
      requestLines.length > 0 &&
      requestLines.some((line) => {
        const offered = quote.lines.find((candidate) => candidate.lineNo === line.lineNo);
        return (
          !offered ||
          compareMoney(money(offered.quantity, 'XXX'), money(line.quantity, 'XXX')) !== 0
        );
      }) &&
      !excludedBecause.includes('incomplete')
    ) {
      excludedBecause.push('incomplete');
    }
    return { quote, excludedBecause };
  });
  const comparable = rows.filter((row) => row.excludedBecause.length === 0);
  const lowest = comparable.reduce<VendorQuote | undefined>(
    (best, row) => (!best || compareMoney(row.quote.total, best.total) < 0 ? row.quote : best),
    undefined,
  );
  return rows.map(({ quote, excludedBecause }) => ({
    vendorId: quote.vendorId,
    revision: quote.revision,
    currency: quote.currency,
    total: quote.total.amount,
    net: quote.totals.net.amount,
    tax: quote.totals.tax.amount,
    charges: quote.totals.charges.amount,
    complete: quote.complete,
    comparable: excludedBecause.length === 0,
    excludedBecause,
    lowest: lowest?.vendorId === quote.vendorId,
    ...(quote.deliveryDays !== undefined ? { deliveryDays: quote.deliveryDays } : {}),
    ...(quote.validUntil ? { validUntil: quote.validUntil } : {}),
  }));
}

/** The line-by-line view of the same comparison. */
export function lineComparison(
  rfq: Pick<Rfq, 'quotes' | 'currency'>,
  requestLines: readonly { lineNo: number; quantity: string }[],
  today?: string,
): z.infer<typeof QuoteLineComparisonSchema>[] {
  const comparable = new Set(
    quoteComparison(rfq, requestLines, today)
      .filter((row) => row.comparable)
      .map((row) => row.vendorId),
  );
  return requestLines.map((line) => {
    const offers = rfq.quotes.flatMap((quote) => {
      const offered = quote.lines.find((candidate) => candidate.lineNo === line.lineNo);
      return offered ? [{ quote, offered }] : [];
    });
    const best = offers
      .filter((entry) => comparable.has(entry.quote.vendorId))
      .reduce<{ net: string } | undefined>(
        (current, entry) =>
          !current || compareMoney(money(entry.offered.net, 'XXX'), money(current.net, 'XXX')) < 0
            ? { net: entry.offered.net }
            : current,
        undefined,
      );
    return {
      lineNo: line.lineNo,
      offers: offers.map(({ quote, offered }) => ({
        vendorId: quote.vendorId,
        quantity: offered.quantity,
        netUnitPrice: offered.netUnitPrice,
        net: offered.net,
        ...(offered.taxRatePercent ? { taxRatePercent: offered.taxRatePercent } : {}),
        complies: offered.complies,
        ...(offered.specification ? { specification: offered.specification } : {}),
        lowest:
          comparable.has(quote.vendorId) &&
          best !== undefined &&
          compareMoney(money(offered.net, 'XXX'), money(best.net, 'XXX')) === 0,
      })),
    };
  });
}

export const RfqViewSchema = z.strictObject({
  rfq: z.lazy(() => RfqSchema),
  comparison: z.array(QuoteComparisonRowSchema),
  lines: z.array(QuoteLineComparisonSchema),
});

/* ------------------------------------------------------------ purchase orders */

/** How an order's supplier and price were decided: an approved award, or an approved direct purchase. */
export const PURCHASE_ROUTES = ['award', 'directPurchase'] as const;
export const PurchaseRouteSchema = z.enum(PURCHASE_ROUTES);
export type PurchaseRoute = z.infer<typeof PurchaseRouteSchema>;

/** Why no quotations are invited — the business reason a direct purchase needs. */
export const DirectPurchaseReasonSchema = z.string().trim().min(10).max(500);

/** A direct purchase's reason, attachment rule, approval and any route change it came from. */
export const DirectPurchaseSchema = z.strictObject({
  reason: z.string(),
  /** Whether a supporting paper had to be attached (the configured rule when it was submitted). */
  attachmentRequired: z.boolean().optional(),
  /** What the approval covers — set at submission; any relevant change needs a new approval. */
  fingerprint: z.string().optional(),
  approvedByAccountId: z.string().optional(),
  approvedAt: InstantSchema.optional(),
  rejection: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  /** The request's quotation route this order replaced: the RFQ cancelled, and why. */
  routeChange: z
    .strictObject({ fromRfqId: RecordIdSchema, fromRfqNumber: z.string(), reason: z.string() })
    .optional(),
});
export type DirectPurchase = z.infer<typeof DirectPurchaseSchema>;

export const PURCHASE_ORDER_STATES = [
  'draft',
  'pendingApproval',
  'approved',
  'partiallyReceived',
  'received',
  'closed',
  'cancelled',
  'rejected',
] as const;
export const PurchaseOrderStateSchema = z.enum(PURCHASE_ORDER_STATES);
export type PurchaseOrderState = z.infer<typeof PurchaseOrderStateSchema>;

export const OrderLineInputSchema = z.strictObject({
  description: z.string().trim().min(2).max(300),
  quantity: QuantitySchema,
  unit: UnitSchema,
  unitPrice: DecimalStringSchema,
  /** The request line it fulfils, when ordered from a request. */
  requestLineNo: z.number().int().positive().optional(),
  itemId: RecordIdSchema.optional(),
  /**
   * A supplier charge rather than goods: freight or other charges. The stable link a delivery's
   * freight cost allocates against — never matched by description or amount.
   */
  chargeKind: z.enum(['freight', 'other']).optional(),
});
export type OrderLineInput = z.infer<typeof OrderLineInputSchema>;

/**
 * Whether an order line is a supplier charge. Lines written before `chargeKind` existed (orders
 * generated from an award before 2026-10-19) are recognised by the generator's own fixed wording.
 */
export function orderLineCharge(line: {
  chargeKind?: 'freight' | 'other' | undefined;
  unit: string;
  description: string;
}): 'freight' | 'other' | undefined {
  if (line.chargeKind) return line.chargeKind;
  if (line.unit !== 'lot') return undefined;
  return line.description === 'Freight'
    ? 'freight'
    : line.description === 'Other charges'
      ? 'other'
      : undefined;
}

export const OrderLineSchema = OrderLineInputSchema.extend({
  lineNo: z.number().int().positive(),
  amount: DecimalStringSchema,
  receivedQuantity: DecimalStringSchema,
  invoicedQuantity: DecimalStringSchema,
});
export type OrderLine = z.infer<typeof OrderLineSchema>;

export const PurchaseOrderSchema = z.strictObject({
  orderId: RecordIdSchema,
  number: z.string(),
  revision: z.number().int().positive(),
  vendorId: RecordIdSchema,
  requestId: RecordIdSchema.optional(),
  rfqId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  costCenter: z.string().optional(),
  currency: z.string(),
  lines: z.array(OrderLineSchema),
  total: MoneySchema,
  deliveryDate: BusinessDateSchema.optional(),
  terms: NoteSchema.optional(),
  /** Retention held on this order's invoices (BD-51 — entered per order; absent = none). */
  retention: RetentionTermsSchema.optional(),
  /** The budget line the order commits against — its own, or its request's. */
  budget: BudgetRefSchema.optional(),
  budgetCheck: BudgetCheckSchema.optional(),
  state: PurchaseOrderStateSchema,
  approvalRequestId: z.string().optional(),
  /**
   * How the supplier and the price were decided: `award` — generated from an approved award;
   * `directPurchase` — no quotations, an explicit direct purchase approved independently. Absent on
   * orders created before 2026-10-19 (a draft of those is declared before submission).
   */
  route: PurchaseRouteSchema.optional(),
  directPurchase: DirectPurchaseSchema.optional(),
  createdByAccountId: z.string(),
  closedReason: z.string().optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type PurchaseOrder = z.infer<typeof PurchaseOrderSchema>;

export const CreatePurchaseOrderSchema = z.strictObject({
  /** Defaults to the request's budget line when the order fulfils a request. */
  budget: BudgetRefSchema.optional(),
  vendorId: RecordIdSchema,
  requestId: RecordIdSchema.optional(),
  rfqId: RecordIdSchema.optional(),
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  costCenter: z.string().trim().max(40).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  lines: z.array(OrderLineInputSchema).min(1).max(100),
  deliveryDate: BusinessDateSchema.optional(),
  terms: NoteSchema.optional(),
  retention: RetentionTermsSchema.optional(),
  /**
   * Required for every order not generated from an award: the explicit direct-purchase route, with
   * its business reason. Approved independently before the order can be issued.
   */
  directPurchase: z.strictObject({ reason: DirectPurchaseReasonSchema }).optional(),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreatePurchaseOrder = z.infer<typeof CreatePurchaseOrderSchema>;

/** Declare (or restate) a draft as a direct purchase, with its business reason. */
export const DeclareDirectPurchaseSchema = z.strictObject({
  reason: DirectPurchaseReasonSchema,
  expectedVersion: z.number().int().positive(),
});
export type DeclareDirectPurchase = z.infer<typeof DeclareDirectPurchaseSchema>;

/** A direct decision on a direct purchase, within the decider's authority limit (BD-57). */
export const DirectPurchaseDecisionSchema = z
  .strictObject({
    decision: z.enum(['approve', 'reject']),
    reason: z.string().trim().min(3).max(500).optional(),
    expectedVersion: z.number().int().positive(),
  })
  .refine((value) => value.decision !== 'reject' || !!value.reason, {
    message: 'REASON_REQUIRED',
    path: ['reason'],
  });
export type DirectPurchaseDecision = z.infer<typeof DirectPurchaseDecisionSchema>;

/** Abandon a request for quotation — the explicit, audited route change to a direct purchase. */
export const CancelRfqSchema = z.strictObject({
  reason: z.string().trim().min(10).max(500),
  expectedVersion: z.number().int().positive(),
});
export type CancelRfq = z.infer<typeof CancelRfqSchema>;

/** An amendment replaces the lines of an approved order nothing was received on; a new revision. */
export const AmendPurchaseOrderSchema = z.strictObject({
  lines: z.array(OrderLineInputSchema).min(1).max(100),
  deliveryDate: BusinessDateSchema.optional(),
  terms: NoteSchema.optional(),
  /** `null` removes retention; absent keeps it. Refused once an invoice was recorded on the order. */
  retention: RetentionTermsSchema.nullable().optional(),
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});

export const ClosePurchaseOrderSchema = CancelSchema;

/* ------------------------------------------------------------------ receipts */

export const GOODS_RECEIPT_STATES = ['recorded', 'reversed'] as const;

export const GoodsReceiptSchema = z.strictObject({
  receiptId: RecordIdSchema,
  number: z.string(),
  orderId: RecordIdSchema,
  vendorId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  receivedOn: BusinessDateSchema,
  warehouseId: RecordIdSchema.optional(),
  lines: z.array(
    z.strictObject({
      lineNo: z.number().int().positive(),
      /** Accepted into the company's stock and against the order: what is invoiced and paid. */
      quantity: DecimalStringSchema,
      /** Delivered but refused at inspection, with why; never received, never invoiced. */
      rejectedQuantity: DecimalStringSchema.optional(),
      rejectionReason: z.string().max(300).optional(),
      /** Sent back to the supplier after acceptance (a return), never above what was accepted. */
      returnedQuantity: DecimalStringSchema.optional(),
      note: z.string().max(300).optional(),
    }),
  ),
  amount: MoneySchema,
  returns: z.array(
    z.strictObject({
      returnId: RecordIdSchema,
      returnedOn: BusinessDateSchema,
      lines: z.array(
        z.strictObject({ lineNo: z.number().int().positive(), quantity: DecimalStringSchema }),
      ),
      amount: MoneySchema,
      reason: z.string(),
      byAccountId: z.string(),
      at: InstantSchema,
    }),
  ),
  state: z.enum(GOODS_RECEIPT_STATES),
  receivedByAccountId: z.string(),
  reversal: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  createdAt: InstantSchema,
});
export type GoodsReceipt = z.infer<typeof GoodsReceiptSchema>;

export const RecordGoodsReceiptSchema = z.strictObject({
  receivedOn: BusinessDateSchema,
  /** The store item lines enter (WH-TRANS-001); required when any received line names an item. */
  warehouseId: RecordIdSchema.optional(),
  lines: z
    .array(
      z
        .strictObject({
          lineNo: z.number().int().positive(),
          /** The accepted quantity. */
          quantity: QuantitySchema,
          rejectedQuantity: QuantitySchema.optional(),
          rejectionReason: z.string().trim().min(3).max(300).optional(),
          note: z.string().trim().max(300).optional(),
        })
        .refine((line) => !line.rejectedQuantity || line.rejectionReason !== undefined, {
          message: 'REJECTION_REASON_REQUIRED',
          path: ['rejectionReason'],
        }),
    )
    .min(1)
    .max(100),
  idempotencyKey: IdempotencyKeySchema,
});

export const ReverseSchema = z.strictObject({ reason: ReasonSchema });

/**
 * Return accepted goods to the supplier (a supplier return): the order's received quantity goes down,
 * the stock leaves the store at the receipt's cost, and the payable is settled by a credit note.
 */
export const ReturnToSupplierSchema = z.strictObject({
  returnedOn: BusinessDateSchema,
  lines: z
    .array(z.strictObject({ lineNo: z.number().int().positive(), quantity: QuantitySchema }))
    .min(1)
    .max(100),
  reason: ReasonSchema,
  idempotencyKey: IdempotencyKeySchema,
});

/* ----------------------------------------------------------- vendor invoices */

export const VENDOR_INVOICE_STATES = [
  'draft',
  'pendingApproval',
  'recorded',
  'partiallyPaid',
  'paid',
  'cancelled',
] as const;
export const VendorInvoiceStateSchema = z.enum(VENDOR_INVOICE_STATES);
export type VendorInvoiceState = z.infer<typeof VendorInvoiceStateSchema>;

/** How a line compares with its order and what was received (PROC-PO-003). */
export const MATCH_STATUSES = [
  'matched',
  'notOnOrder',
  'quantityExceedsReceived',
  'priceDiffers',
] as const;
export const MatchStatusSchema = z.enum(MATCH_STATUSES);
export type MatchStatus = z.infer<typeof MatchStatusSchema>;

export const VendorInvoiceLineInputSchema = InvoiceLineInputSchema.extend({
  /** The order line it bills; absent for a non-order invoice (then the match is `notOnOrder`). */
  orderLineNo: z.number().int().positive().optional(),
});
export type VendorInvoiceLineInput = z.infer<typeof VendorInvoiceLineInputSchema>;

export const VendorInvoiceLineSchema = InvoiceLineSchema.extend({
  orderLineNo: z.number().int().positive().optional(),
  match: MatchStatusSchema,
});

/**
 * `creditNote`: the supplier's credit against one recorded invoice — a price correction or goods
 * returned. It reduces what that invoice still owes and never pays anything out.
 */
export const VENDOR_INVOICE_KINDS = ['invoice', 'creditNote'] as const;

export const VendorInvoiceSchema = z.strictObject({
  vendorInvoiceId: RecordIdSchema,
  kind: z.enum(VENDOR_INVOICE_KINDS),
  /** For a credit note: the invoice it credits. */
  creditsInvoiceId: RecordIdSchema.optional(),
  /** Receipts this invoice bills (accepted quantities), for the three-way view. */
  receiptIds: z.array(RecordIdSchema),
  /** Credit notes recorded against this invoice. */
  credited: MoneySchema,
  number: z.string(),
  /** The vendor's own invoice number, unique per vendor. */
  vendorReference: z.string(),
  vendorId: RecordIdSchema,
  orderId: RecordIdSchema.optional(),
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  invoiceDate: BusinessDateSchema,
  dueDate: BusinessDateSchema.optional(),
  currency: z.string(),
  lines: z.array(VendorInvoiceLineSchema),
  totals: z.strictObject({ net: MoneySchema, tax: MoneySchema, gross: MoneySchema }),
  /** True when every line is `matched`. */
  matched: z.boolean(),
  paidAmount: MoneySchema,
  /** Amount on payment requests not yet executed, rejected or cancelled. */
  requestedAmount: MoneySchema,
  /** Retention held from this invoice under its order's terms, and the part released since. */
  retention: z
    .strictObject({
      percent: z.string(),
      base: RetentionBaseSchema,
      held: MoneySchema,
      released: MoneySchema,
      /** A release waiting for its approval (BD-51 through `procurement.retention.release`). */
      pendingRelease: z
        .strictObject({ amount: MoneySchema, reason: z.string(), approvalRequestId: z.string() })
        .optional(),
    })
    .optional(),
  /** Advances paid to the vendor and recovered against this invoice. */
  advanceApplied: MoneySchema,
  advanceApplications: z.array(
    z.strictObject({
      advanceId: RecordIdSchema,
      amount: MoneySchema,
      byAccountId: z.string(),
      at: InstantSchema,
    }),
  ),
  retentionReleases: z.array(
    z.strictObject({
      amount: MoneySchema,
      reason: z.string(),
      byAccountId: z.string(),
      at: InstantSchema,
    }),
  ),
  /** What is due to the vendor now: gross − retention held + released − advances applied − credited. */
  payable: MoneySchema,
  /** payable − paid − requested: what a new payment request may still ask for. */
  outstanding: MoneySchema,
  state: VendorInvoiceStateSchema,
  approvalRequestId: z.string().optional(),
  recordedByAccountId: z.string(),
  cancellation: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type VendorInvoice = z.infer<typeof VendorInvoiceSchema>;

export const CreateVendorInvoiceSchema = z.strictObject({
  vendorId: RecordIdSchema,
  vendorReference: z.string().trim().min(1).max(60),
  kind: z.enum(VENDOR_INVOICE_KINDS).default('invoice'),
  creditsInvoiceId: RecordIdSchema.optional(),
  receiptIds: z.array(RecordIdSchema).max(50).default([]),
  orderId: RecordIdSchema.optional(),
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  invoiceDate: BusinessDateSchema,
  dueDate: BusinessDateSchema.optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  lines: z.array(VendorInvoiceLineInputSchema).min(1).max(100),
  idempotencyKey: IdempotencyKeySchema,
});
export type CreateVendorInvoice = z.infer<typeof CreateVendorInvoiceSchema>;

export const ApplyAdvanceSchema = z.strictObject({
  advanceId: RecordIdSchema,
  amount: MoneySchema,
  expectedVersion: z.number().int().positive(),
});

export const ReleaseRetentionSchema = z.strictObject({
  amount: MoneySchema,
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});

/* ------------------------------------------------------------------ advances */

export const VENDOR_ADVANCE_STATES = ['open', 'cancelled'] as const;
export const VendorAdvanceStateSchema = z.enum(VENDOR_ADVANCE_STATES);

/**
 * An advance (down payment) to a vendor against an approved order (PROC-PO-004). It is paid through a
 * treasury payment request like any payment (BD-59); what was paid is then **applied** to the order's
 * invoices — never more than was paid, never twice, never beyond what an invoice still owes.
 */
export const VendorAdvanceSchema = z.strictObject({
  advanceId: RecordIdSchema,
  number: z.string(),
  vendorId: RecordIdSchema,
  orderId: RecordIdSchema,
  legalEntityId: RecordIdSchema,
  branchId: RecordIdSchema,
  projectId: RecordIdSchema.optional(),
  amount: MoneySchema,
  paid: MoneySchema,
  applied: MoneySchema,
  /** paid − applied: what may still be applied to an invoice. */
  available: MoneySchema,
  /** On payment requests not yet executed, rejected or cancelled. */
  requested: MoneySchema,
  purpose: z.string(),
  state: VendorAdvanceStateSchema,
  requestedByAccountId: z.string(),
  cancellation: z
    .strictObject({ reason: z.string(), byAccountId: z.string(), at: InstantSchema })
    .optional(),
  version: z.number().int().positive(),
  createdAt: InstantSchema,
  updatedAt: InstantSchema,
});
export type VendorAdvance = z.infer<typeof VendorAdvanceSchema>;

export const RequestVendorAdvanceSchema = z.strictObject({
  amount: MoneySchema,
  fromAccountId: RecordIdSchema,
  method: z.enum(['bankTransfer', 'cash', 'cheque']),
  purpose: z.string().trim().min(3).max(300),
  idempotencyKey: IdempotencyKeySchema,
});

export const CancelAdvanceSchema = z.strictObject({
  reason: ReasonSchema,
  expectedVersion: z.number().int().positive(),
});

export const RequestVendorPaymentSchema = z.strictObject({
  fromAccountId: RecordIdSchema,
  amount: MoneySchema,
  method: z.enum(['bankTransfer', 'cash', 'cheque']),
  idempotencyKey: IdempotencyKeySchema,
});

/* ------------------------------------------------------- statement and ageing */

export const VendorStatementSchema = z.strictObject({
  vendorId: RecordIdSchema,
  currency: z.string(),
  rows: z.array(
    z.strictObject({
      date: BusinessDateSchema,
      kind: z.enum(['invoice', 'payment', 'cancellation', 'advancePayment']),
      reference: z.string(),
      /** What the company owes more (an invoice) or less (a payment, a cancellation). */
      amount: MoneySchema,
      balance: MoneySchema,
    }),
  ),
  balance: MoneySchema,
  /** Retention held and not yet released — owed, but not yet due (BD-51). */
  retentionHeld: MoneySchema,
  /** Advances paid and not yet applied to an invoice. */
  advancesUnapplied: MoneySchema,
  generatedAt: InstantSchema,
});
export type VendorStatement = z.infer<typeof VendorStatementSchema>;

export const PAYABLES_AGEING_BUCKETS = [
  'notDue',
  'days1to30',
  'days31to60',
  'days61to90',
  'over90',
] as const;

export const PayablesAgeingSchema = z.strictObject({
  asOf: BusinessDateSchema,
  rows: z.array(
    z.strictObject({
      vendorId: RecordIdSchema,
      vendorName: z.string(),
      currency: z.string(),
      buckets: z.record(z.enum(PAYABLES_AGEING_BUCKETS), MoneySchema),
      total: MoneySchema,
      /** Retention held, owed but not due until released — never aged. */
      retention: MoneySchema,
      /** Advances paid and not yet applied. */
      advancesUnapplied: MoneySchema,
    }),
  ),
  generatedAt: InstantSchema,
});
export type PayablesAgeing = z.infer<typeof PayablesAgeingSchema>;

/** The bucket an outstanding amount falls in on `asOf`, by its due date (or invoice date). */
export function ageingBucket(due: string, asOf: string): (typeof PAYABLES_AGEING_BUCKETS)[number] {
  const days = Math.round(
    (Date.parse(`${asOf}T00:00:00Z`) - Date.parse(`${due}T00:00:00Z`)) / 86_400_000,
  );
  if (days <= 0) return 'notDue';
  if (days <= 30) return 'days1to30';
  if (days <= 60) return 'days31to60';
  if (days <= 90) return 'days61to90';
  return 'over90';
}

/* ------------------------------------------------------------ approvals, audit */

export const PROCUREMENT_APPROVAL_OPERATIONS = {
  request: 'procurement.request.approve',
  order: 'procurement.order.approve',
  /** An invoice whose lines do not match order and receipt exactly (BD-47). */
  matchException: 'procurement.invoice.matchException',
  /** Releasing retention (BD-51): proceeds at once unless a published policy governs it. */
  retentionRelease: 'procurement.retention.release',
  /**
   * Awarding a supplier's quotation (owner-approved 2026-10-17): always approved by someone other
   * than the person who awarded it, under a published policy (an executive, finance). Without a policy
   * the award is refused (`AWARD_POLICY_NOT_CONFIGURED`) and no order can be generated; drafts,
   * quotations and the comparison stay available. The approval covers the award's fingerprint.
   */
  award: 'procurement.award.approve',
  /**
   * A direct purchase — an order without quotations (owner closure of 2026-10-19): approved by
   * someone other than its preparer and the request's requester, under a published policy or
   * directly within a configured authority limit. With neither it cannot be issued
   * (`DIRECT_PURCHASE_APPROVAL_NOT_CONFIGURED`); the draft stays. The approval covers the order's
   * fingerprint — supplier, currency, lines, quantities, prices, charges and terms.
   */
  directPurchase: 'procurement.directPurchase.approve',
} as const;

export const PROCUREMENT_AUDIT_ACTIONS = {
  vendorCreated: 'procurement.vendor.created',
  vendorUpdated: 'procurement.vendor.updated',
  vendorStateChanged: 'procurement.vendor.stateChanged',
  vendorPayeeLinked: 'procurement.vendor.payeeLinked',
  requestCreated: 'procurement.request.created',
  requestStateChanged: 'procurement.request.stateChanged',
  rfqCreated: 'procurement.rfq.created',
  rfqCancelled: 'procurement.rfq.cancelled',
  directPurchaseDeclared: 'procurement.order.directPurchaseDeclared',
  directPurchaseDecided: 'procurement.order.directPurchaseDecided',
  quoteRecorded: 'procurement.rfq.quoteRecorded',
  rfqAwarded: 'procurement.rfq.awarded',
  awardRequested: 'procurement.rfq.awardRequested',
  awardInvalidated: 'procurement.rfq.awardInvalidated',
  awardRejected: 'procurement.rfq.awardRejected',
  orderFromAward: 'procurement.order.fromAward',
  goodsReturned: 'procurement.receipt.goodsReturned',
  creditNoteRecorded: 'procurement.invoice.creditNoteRecorded',
  orderCreated: 'procurement.order.created',
  orderStateChanged: 'procurement.order.stateChanged',
  orderAmended: 'procurement.order.amended',
  receiptRecorded: 'procurement.receipt.recorded',
  receiptReversed: 'procurement.receipt.reversed',
  invoiceCreated: 'procurement.invoice.created',
  invoiceRecorded: 'procurement.invoice.recorded',
  invoiceCancelled: 'procurement.invoice.cancelled',
  paymentRequested: 'procurement.invoice.paymentRequested',
  paymentSettled: 'procurement.invoice.paymentSettled',
  advanceRequested: 'procurement.advance.requested',
  advanceSettled: 'procurement.advance.paymentSettled',
  advanceCancelled: 'procurement.advance.cancelled',
  advanceApplied: 'procurement.invoice.advanceApplied',
  retentionReleaseRequested: 'procurement.invoice.retentionReleaseRequested',
  retentionReleased: 'procurement.invoice.retentionReleased',
  refused: 'procurement.refused',
} as const;
